import {Injectable} from '@angular/core';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {Lot} from '../models/lot';
import {LetterAnnuity} from '../models/letter-annuity';
import {AngularFireStorage, AngularFireStorageReference, AngularFireUploadTask} from '@angular/fire/compat/storage';
import {finalize} from 'rxjs/operators';
import {CryptoV2Service} from './crypto-v2.service';

interface Order {
  id: string;
  product_id: string;
  member_id: string;
  value: number;
}

interface Member {
  id: string;
  name: string;
  situation: string;
  annuity_from: string;
  subCategory_id: string,
  subCategory_name: string;
  annuities: any[],
}

@Injectable({
  providedIn: 'root'
})
export class AnnuityLetterService {


  constructor (
    private db: AngularFirestore,
    private fs: AngularFireStorage,
    private _cryptoV2Service: CryptoV2Service
  ) { }

  async loadCategories () {
    const array = [];
    const querySnapshot = await this.db.collection('sbccv_subcategories', ref => ref
      .where('annuity', '==', true)
      .orderBy("name"))
      .get()
      .toPromise();

    querySnapshot.forEach(async (doc: any) => {
      array.push({
        "id": doc.id,
        "name": doc.data().name
      })
    })
    return array;
  }

  async loadAnnuities () {
    const array = [];
    const querySnapshot = await this.db.collection<any>('sbccv_products', ref => ref
      .where('isAnnuity', '==', true)
      .orderBy('annuity_from', 'desc'))
      .get()
      .toPromise();

    querySnapshot.forEach(async (item) => {
      array.push({
        "id": item.id,
        "name": item.data().name,
        "annuity_from": item.data().annuity_from
      })

    })
    return array;
  }

  async loadCurrentLot (annuity_id: any) {
    const querySnapshot = await this.db.collection<Lot>('sbccv_product_lots', ref => ref
      .where('product_id', '==', annuity_id)
      .limit(1))
      .get()
      .toPromise();

    return {
      "id": querySnapshot.docs[ 0 ].id,
      "product_id": querySnapshot.docs[ 0 ].data().product_id,
      "until_date": querySnapshot.docs[ 0 ].data().until_date,
    }

  }

  async loadCurrentPrices (lot_id: any) {
    const array = [];
    const querySnapshot = await this.db.collection<Lot>('sbccv_product_cat_price', ref => ref
      .where('lot_id', '==', lot_id))
      .get()
      .toPromise();

    querySnapshot.forEach(async (item: any) => {

      item.data().categories.forEach((category) => {
        array.push({
          id: category.id,
          name: category.name,
          value: item.data().value,
          value_pix: item.data().value_pix
        })
      })
    })
    return array;

  }

  /**
     * Obtém todas as ordens relacionadas a anuidades
     * @param annuities Array com os IDs das anuidades
     * @returns Promise com array de ordens de anuidades
     */
  async getAnnuityOrders (annuities: any[]): Promise<Order[]> {
    if (!annuities || annuities.length === 0) {
      return [];
    }

    try {
      // Usamos Promise.all para executar todas as consultas em paralelo
      const ordersPromises = annuities.map(annuity =>
        this.getOrdersByProductId(annuity.id)
      );

      const ordersArrays = await Promise.all(ordersPromises);

      // Achata o array de arrays em um único array
      // return ordersArrays.flat();
      return ([] as Order[]).concat(...ordersArrays);
    } catch (error) {
      console.error('Error fetching annuity orders:', error);
      throw error;
    }
  }

  /**
   * Obtém ordens por product_id (privado pois é usado apenas internamente)
   * @param productId ID do produto/anuidade
   * @returns Promise com array de ordens para o product_id especificado
   */
  private async getOrdersByProductId (productId: string): Promise<Order[]> {

    try {
      const querySnapshot = await this.db.collection<Order>('sbccv_orders', ref => ref
        .where('product_id', '==', productId)
        .where('returnCode', '==', "00")
      )
        .get()
        .toPromise();

      if (!querySnapshot || querySnapshot.empty) {
        return [];
      }

      return querySnapshot.docs.map((doc: any) => ({
        id: doc.id,
        product_id: doc.data().product_id,
        product_name: doc.data().product_name,
        member_id: doc.data().member_id,
        value: doc.data().value,
        origin: doc.data().origin,
        type: doc.data().type,
      }));
    } catch (error) {
      console.error(`Error fetching orders for product ${ productId }:`, error);
      throw error;
    }
  }

  /**
     * Obtém todas as ordens relacionadas a anuidades
     * @param annuities Array com os IDs das anuidades
     * @returns Promise com array de ordens de anuidades
     */
  async getMembersAnnuity (categories: any[]): Promise<Member[]> {
    if (!categories || categories.length === 0) {
      return [];
    }

    try {
      // Usamos Promise.all para executar todas as consultas em paralelo
      const membersPromises = categories.map(category =>
        this.getMembersByCategoryId(category.id)
      );

      const membersArrays = await Promise.all(membersPromises);

      // Achata o array de arrays em um único array
      // return ordersArrays.flat();
      const membersArray = ([] as Member[]).concat(...membersArrays);
      membersArray.sort(this.compareNames)

      return membersArray
      //
    } catch (error) {
      console.error('Error fetching members by category: ', error);
      throw error;
    }
  }

  /**
   * Obtém members por category_id (privado pois é usado apenas internamente)
   * @param categoryId ID da categoria
   * @returns Promise com array de members para o category_id especificado
   */
  private async getMembersByCategoryId (categoryId: string): Promise<Member[]> {
    try {
      const querySnapshot = await this.db.collection<Order>('sbccv_members', ref => ref
        .where('subCategory.id', '==', categoryId)
        .orderBy("name", "asc")
      )
        .get()
        .toPromise();

      if (!querySnapshot || querySnapshot.empty) {
        return [];
      }

      const filteredDocs = querySnapshot.docs.filter((doc: any) =>
        !doc.data().hasOwnProperty('deleted') || doc.data().deleted === false
      );

      return filteredDocs.map((doc: any) => ({
        id: doc.id,
        name: doc.data().name,
        email: doc.data().email,
        cpf: doc.data().cpf,
        cellPhone: doc.data().cellPhone,

        credit: doc.data().credit ? doc.data().credit : 0,
        situation: this.formatSituation(doc.data().situation.toString()),
        annuity_from: doc.data().annuity_from,
        subCategory_id: doc.data().subCategory.id,
        subCategory_name: doc.data().subCategory.name,

        address: doc.data().address,
        number: doc.data().number,
        complement: doc.data().complement,
        district: doc.data().district,
        city: doc.data().city,
        state: doc.data().state,
        country: doc.data().country,
        postalCode: doc.data().postalCode,

        foreignCustomer: doc.data().foreignCustomer ? doc.data().foreignCustomer : false,
        tester: doc.data().tester ? doc.data().tester : false,
        staff: doc.data().staff ? doc.data().staff : false,

        letter_id: null,
        dateSent: null,
        dateRead: null,
        datePayed: null,
        url: "",
        annuities: [],
      }));
    } catch (error) {
      console.error(`Error fetching members for category ${ categoryId }:`, error);
      throw error;
    }
  }

  private formatSituation (situationValue: string): string {
    const situations = {
      '0': 'Quite',
      '1': 'Não-Quite',
      '2': 'Isento'
    };

    return situations[ situationValue ] || 'Desconhecido';
  }

  compareNames (a, b) {
    if (a.name > b.name) return 1;
    if (b.name > a.name) return -1;
    return 0;
  }

  compareProducts (a, b) {
    if (a.product_name > b.product_name) return -1;
    if (b.product_name > a.product_name) return 1;
    return 0;
  }

  async makeMemberStatement (members: any[], annuities: any[], payedAnnuities: any[]): Promise<any[]> {
    const memberPayedAnnuities = []

    members.forEach((member) => {

      member.annuities = payedAnnuities.filter((e: any) => {
        return e.member_id == member.id;
      })
      // 
      //  console.log(member.name)
      annuities.forEach((annuity) => {
        if (annuity.annuity_from >= member.annuity_from) {
          // console.log(annuity.annuity_from)
          const payed = member.annuities.find((e) => {
            return annuity.id == e.product_id;
          })
          if (payed) {
            if (payed.value.toString() === "0") {
              payed.isFree = true;
            }
          } else if (!payed) {
            member.annuities.unshift({
              id: null,
              product_id: annuity.id,
              product_name: annuity.name,
              member_id: member.id,
              value: 0,
              isFree: false
            })
          }
        }
      })

      member.annuities.sort(this.compareProducts);

      // caso não haja pendências, não enviar carta,
      // desabilitar checkbox nna listagem
      let mustSend: boolean = false;
      for (const annuity of member.annuities) {
        if (annuity.value == 0 && !annuity.isFree) {
          mustSend = true
          break;
        }
      }
      member.mustSend = mustSend;
      //
      memberPayedAnnuities.push(member)
    })

    return memberPayedAnnuities;
  }

  createLetter (data: any): LetterAnnuity {
    return {
      id: data.id,
      annuity_id: data.annuity_id,
      name: data.name,
      subject: data.subject,
      text: data.text,
      image: data.image,
      deleted: data.deleted,
      created: data.created
    }
  }

  loadAnnuityLetters (annuity_id: any) {
    return this.db.collection<LetterAnnuity>('sbccv_annuity_letters', ref => ref
      .where('annuity_id', '==', annuity_id)
      .where('deleted', '==', false)
      .orderBy("created", "desc"))
      .snapshotChanges();
  }

    loadAllAnnuityLetters () {
    return this.db.collection<LetterAnnuity>('sbccv_annuity_letters', ref => ref
      .where('deleted', '==', false)
      .orderBy("created", "desc"))
      .snapshotChanges();
  }

  async loadAnnuityLettersSent (letter_id: any) {
    const array = [];
    const querySnapshot = await this.db.collection<Lot>('sbccv_annuity_letters_sent', ref => ref
      .where('letter_id', '==', letter_id))
      .get()
      .toPromise();

    querySnapshot.forEach(async (item: any) => {
      array.push({
        id: item.id,
        member_id: item.data().id,
        datePayed: item.data().datePayed,
        dateRead: item.data().dateRead,
        dateSent: item.data().dateSent,
        url: item.data().url
      })
    })
    return array;

  }

  async insert (entity: LetterAnnuity) {
    return this.db.collection('sbccv_annuity_letters').add(Object.assign({}, entity));
  }

  async update (entity: LetterAnnuity) {
    let id = entity.id;
    await this.db.collection('sbccv_annuity_letters').doc(id).update(Object.assign({}, entity));
  }

  uploadImage (file, letter_id) {

    const fsRef: AngularFireStorageReference = this.fs.ref("images/annuities/letters/" + letter_id + ".png");
    const fsTask: AngularFireUploadTask = fsRef.put(file);

    fsTask.snapshotChanges().pipe(
      finalize(() => {
        fsRef.getDownloadURL().subscribe(url => {
          this.updateImage(letter_id, url)
        });
      }))
      .subscribe();
  }

  private async updateImage (letter_id: any, image: any) {
    try {
      await this.db.collection('sbccv_annuity_letters').doc(letter_id).set({image: image}, {merge: true});
      // this.objectImage = "";
    } catch (err) {
      console.log("erro upadate image: " + JSON.stringify(err));
    }
  }

  deleteLetter (id: any) {
    return this.db.collection('sbccv_annuity_letters').doc(id).update({'deleted': true});
  }

  async deleteLetterSent (id: any) {
    console.log("deleteLetterSent: " + id)
    return await this.db.collection('sbccv_annuity_letters_sent').doc(id).delete();
  }

  async deleteOrder (id: any) {
    console.log("deleteOrder: " + id)
    return await this.db.collection('sbccv_orders').doc(id).delete();
  }

  getLetterSentById (letter_sent_id: string): Promise<any> {
    return this.db.collection('sbccv_annuity_letters_sent').doc(letter_sent_id)
      .get()
      .toPromise()
      .then((result: any) => {
        if (result.exists) {
          return {...result.data(), "id": result.id};
        } else {
          return null;
        }
      });
  }

  getLetterSnapshotChanges (letter_sent_id: string) {
    return this.db.collection('sbccv_annuity_letters_sent').doc(letter_sent_id)
      .snapshotChanges();
  }

  async updateLetterSentDate (id: string) {
    await this.db.collection('sbccv_annuity_letters_sent').doc(id).update({dateSent: new Date()});
  }


  crypto (data) {
    try {
      return this._cryptoV2Service.encryptData(data)
    } catch (error) {
      console.error('Error encryptData');
      throw error;
    }
  }

  decrypto (data) {
    try {
      return this._cryptoV2Service.decryptData(data)
    } catch (error) {
      console.error('Error decryptData');
      throw error;
    }
  }

  makeUrl (member_id, current_annuity) {
    return "https://sbccv-proj.firebaseapp.com/annuity-back/" + member_id + ":" + current_annuity
  }

  //  await this.delay(3000);
  async delay (ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async registerLetter (data: any) {
    const key = data.id + ":" + data.annuity_id
    return this.db.collection('sbccv_annuity_letters_sent').doc(key).set(data);
  }

  async createOrder (product_id, product_name: string, item: any) {

    let countNotFree = 0;
    item.annuities.forEach((annuity: any) => {
      if (!annuity.isFree && annuity.value == 0) {
        countNotFree++
      }
    })

    const dismember: boolean = (countNotFree > 1) ? true : false;

    console.log("dismember: " + dismember)

    const order: any = { // Ordered

      date: new Date(),
      type: "",

      payAsOverdue: null,
      dismember: dismember,

      product_id: product_id,
      product_name: product_name,

      member_id: item.id,
      member_name: item.name,
      member_cpf: item.cpf,
      member_cellPhone: item.cellPhone,
      member_email: item.email,
      member_debit: 0,
      category_id: item.subCategory_id,
      category_name: item.subCategory_name,

      returnCode: "pending",
      staff: item.staff ? item.staff : false,
    };

    return await this.db.collection('sbccv_orders').add(order)
      .then((result) => {
        order.id = result.id;
        return order;
      })
      .catch((err) => {
        console.log("error create order")
      })


  }

  getCardBrand (cardNumber: string): string {
    const cleanedNumber = cardNumber.replace(/\D/g, '');

    // Padrões das bandeiras mais comuns
    const patterns = [
      {brand: 'Visa', pattern: /^4/},
      {brand: 'Master', pattern: /^5[1-5]/},
      {brand: 'Amex', pattern: /^3[47]/},
      {brand: 'Discover', pattern: /^6(?:011|5)/},
      {brand: 'Diners', pattern: /^3(?:0[0-5]|[68])/},
      {brand: 'JCB', pattern: /^(?:2131|1800|35\d{3})/},
      {brand: 'Elo', pattern: /^(401178|401179|431274|438935|451416|457393|457631|457632|504175|627780|636297|636368|(506699|5067[0-6]\d|50677[0-8])|(50900\d|5090[1-9]\d)|5094\d{2}|509[5-8]\d{2}|5099[0-8]\d)|50999[0-9]|65003[1-3]|650035[0-4]|65003[5-9]|65004\d{2}|65005[0-1]\d{10}$/},
      {brand: 'Hipercard', pattern: /^(384100|384140)/}
    ];

    for (const item of patterns) {
      if (item.pattern.test(cleanedNumber)) {
        return item.brand;
      }
    }
  }

  async getOrdersByMemberAndProductId (memberId: string, productId: string): Promise<any[]> {
    try {
      const querySnapshot = await this.db.collection<Order>('sbccv_orders', ref => ref
        .where('product_id', '==', productId)
        .where('member_id', '==', memberId)
        .where('returnCode', '==', "00")
        .limit(1)
      )
        .get()
        .toPromise();

      if (!querySnapshot || querySnapshot.empty) {
        return [];
      }

      return querySnapshot.docs.map((doc: any) => ({
        id: doc.id,
        product_id: doc.data().product_id,
        product_name: doc.data().product_name,
        member_id: doc.data().member_id,
        member_name: doc.data().member_name,
        value: doc.data().value,
        type: doc.data().type,
        invoiceUrl: doc.data().invoiceUrl,
        receiptUrl: doc.data().receiptUrl,
      }));
    } catch (error) {
      console.error(`Error fetching orders for product ${ productId }:`, error);
      throw error;
    }
  }


  async getOrdersPendingByMemberAndProductId (memberId: string, productId: string): Promise<any[]> {
    try {
      const querySnapshot = await this.db.collection<Order>('sbccv_orders', ref => ref
        .where('member_id', '==', memberId)
        .where('product_id', '==', productId)
        .where('returnCode', '==', "pending")
        .limit(1)
      )
        .get()
        .toPromise();

      if (!querySnapshot || querySnapshot.empty) {
        return [];
      }

      return querySnapshot.docs.map((doc: any) => ({
        id: doc.id,
        product_id: doc.data().product_id,
        product_name: doc.data().product_name,
        member_id: doc.data().member_id,
        member_name: doc.data().member_name,
        returnCode: doc.data().returnCode,
      }));
    } catch (error) {
      console.error(`Error fetching pending orders for product ${ productId } and mmber ${ memberId }:`, error);
      throw error;
    }
  }

}
